import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ $isExiting: boolean; $variant: 'warning' | 'error' | 'success' | 'info' }>`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 14px 20px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: 10000;
  animation: ${({ $isExiting }) => ($isExiting ? slideOut : slideIn)} 0.3s ease forwards;

  background-color: ${({ $variant }) => {
    switch ($variant) {
      case 'warning': return '#FFFBEB';
      case 'error': return '#FEF2F2';
      case 'success': return '#F0FDF4';
      case 'info': return '#EFF6FF';
      default: return '#FFFBEB';
    }
  }};
  color: ${({ $variant }) => {
    switch ($variant) {
      case 'warning': return '#92400E';
      case 'error': return '#991B1B';
      case 'success': return '#166534';
      case 'info': return '#1E40AF';
      default: return '#92400E';
    }
  }};
  border: 1px solid ${({ $variant }) => {
    switch ($variant) {
      case 'warning': return '#FDE68A';
      case 'error': return '#FECACA';
      case 'success': return '#BBF7D0';
      case 'info': return '#BFDBFE';
      default: return '#FDE68A';
    }
  }};
`;

const ToastIcon = styled.span`
  font-size: 18px;
`;

const ToastContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToastTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const ToastMessage = styled.div`
  font-size: 13px;
  opacity: 0.8;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
  font-size: 18px;
  line-height: 1;
  font-weight: 600;
  opacity: 0.5;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

interface ToastProps {
  title: string;
  message?: string;
  variant?: 'warning' | 'error' | 'success' | 'info';
  duration?: number;
  onClose: () => void;
}

export function Toast({ title, message, variant = 'warning', duration = 5000, onClose }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, duration - 300); // Start exit animation 300ms before close

    const closeTimer = setTimeout(() => {
      onClose();
    }, duration);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(closeTimer);
    };
  }, [duration, onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 300);
  };

  const icons = {
    warning: '⚠️',
    error: '❌',
    success: '✅',
    info: 'ℹ️',
  };

  return (
    <ToastContainer $isExiting={isExiting} $variant={variant}>
      <ToastIcon>{icons[variant]}</ToastIcon>
      <ToastContent>
        <ToastTitle>{title}</ToastTitle>
        {message && <ToastMessage>{message}</ToastMessage>}
      </ToastContent>
      <CloseButton onClick={handleClose}>&times;</CloseButton>
    </ToastContainer>
  );
}
